import React, { useEffect, useState, useMemo } from "react";
import {
  Trash2,
  Edit,
  Search,
  Table2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Printer,
  IndianRupee,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Apiurl from "../../environment/Apiurl";

function SalePharmacy({ toggleSidebar }) {
  const navigate = useNavigate();
  const [responseData, setResponseData] = useState([]);
  const [filterType, setFilterType] = useState("All"); // Filter by Homeo or Yoga, default "All"
  const [dateFilter, setDateFilter] = useState("today");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntries, setShowEntries] = useState("10");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);
  const [userPrivileges, setUserPrivileges] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [updatedPayments, setUpdatedPayments] = useState([]);
  const [deletedPayments, setDeletedPayments] = useState([]);
  const [editingPayment, setEditingPayment] = useState(null);
  const [newPayment, setNewPayment] = useState({
    paid_date: new Date().toISOString().split("T")[0],
    pay_type: "",
    paid_amount: "",
  });

  // Fetch user privileges
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRole = localStorage.getItem("role_name") || "unknown";
      console.log("Fetching privileges for role_name:", userRole);

      if (userRole === "unknown") {
        console.warn("No role_name found in localStorage");
        setError(
          "No role assigned to the user. Please contact the administrator."
        );
        setTimeout(() => setError(null), 3000);
        return;
      }

      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log(
        "Fetched roles data:",
        JSON.stringify(response.data, null, 2)
      );

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error(
          `Invalid or empty roles data received: ${JSON.stringify(
            response.data
          )}`
        );
      }

      const roleData = response.data.find(
        (role) => role.role_name === userRole
      );

      if (roleData) {
        console.log(
          "Found role data for role_name:",
          userRole,
          JSON.stringify(roleData, null, 2)
        );
        const privileges = {};
        roleData.pages.forEach((page) => {
          const pageName = page.page;
          const pagePrivileges = page.priviallages || page.privileges || {};
          privileges[pageName] = {
            access: page.access,
            ...pagePrivileges,
          };
          console.log(
            `Processing page: ${pageName}, Access: ${page.access}, Privileges:`,
            JSON.stringify(pagePrivileges, null, 2)
          );
        });
        console.log(
          "Set user privileges:",
          JSON.stringify(privileges, null, 2)
        );
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_name:", userRole);
        setError("No role data found for the current user.");
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error(
        "Error fetching user privileges:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to fetch user privileges. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Check permission
  const hasPermission = (permission) => {
    const salePrivileges = userPrivileges["Sale Pharmacy"];
    if (!salePrivileges) {
      console.log("No privileges found for Sale Pharmacy page");
      return false;
    }
    console.log(
      `Checking permission '${permission}' for Sale Pharmacy:`,
      JSON.stringify(salePrivileges, null, 2)
    );
    if (permission === "access") {
      const accessGranted = salePrivileges.access === true;
      console.log(`Access permission result: ${accessGranted}`);
      return accessGranted;
    }
    const hasPerm =
      salePrivileges.all === true || salePrivileges[permission] === true;
    console.log(`Permission '${permission}' result: ${hasPerm}`);
    return hasPerm;
  };

  // Fetch sale data and sort by invoice_number in descending order
  const fetchData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Sale Pharmacy page");
      setError("You do not have permission to view sale records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Fetching sale data from:", `${Apiurl}/sale_pharmacy`);
      const response = await axios.get(`${Apiurl}/sale_pharmacy`);
      console.log("Fetched sale data:", JSON.stringify(response.data, null, 2));
      // Sort data by invoice_number in descending order
      // const sortedData = [...(response.data || [])].sort((a, b) =>
      //   b.invoice_number.localeCompare(a.invoice_number)
      // );

      const sortedData = [...(response.data || [])].sort((a, b) => {
        const numA = Number(a.invoice_number.split("-").pop());
        const numB = Number(b.invoice_number.split("-").pop());
        return numB - numA; // descending
      });

      setResponseData(sortedData);
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching sale data:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to fetch sale data. Please try again.");
      setTimeout(() => setError(null), 3000);
      setResponseData([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get current payments (merged with updates)
  const getCurrentPayments = useMemo(() => {
    if (!selectedInvoice) return [];
    const existing = (selectedInvoice.sales_pharmacy_payment || []).filter(
      (p) => !deletedPayments.includes(p._id)
    );
    const mergedExisting = existing.map((p) => {
      const updated = updatedPayments.find((up) => up._id === p._id);
      return updated
        ? {
            ...p,
            paid_date: updated.paid_date,
            pay_type: updated.pay_type,
            paid_amount: updated.paid_amount,
          }
        : p;
    });
    return [...mergedExisting, ...pendingPayments];
  }, [selectedInvoice, pendingPayments, updatedPayments, deletedPayments]);

  const currentTotalPaid = useMemo(
    () =>
      getCurrentPayments.reduce(
        (sum, payment) => sum + parseFloat(payment.paid_amount || 0),
        0
      ),
    [getCurrentPayments]
  );

  const currentBalance = useMemo(
    () => (selectedInvoice?.net_total || 0) - currentTotalPaid,
    [selectedInvoice?.net_total, currentTotalPaid]
  );

  // Handle Rupee icon click to open payment modal
  const handlePaymentClick = (sale) => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Sale Pharmacy page");
      setError("You do not have permission to add payment records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening payment modal for invoice:", sale.invoice_number);
    setSelectedInvoice(sale);
    setPendingPayments([]);
    setUpdatedPayments([]);
    setDeletedPayments([]);
    setEditingPayment(null);
    setNewPayment({
      paid_date: new Date().toISOString().split("T")[0],
      pay_type: "",
      paid_amount: "",
    });
    setShowAddModal(true);
  };

  // Handle form input changes for new or editing payment
  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setNewPayment((prev) => ({ ...prev, [name]: value }));
  };

  // Handle adding new payment to pending list
  const handleAddPayment = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Sale Pharmacy page");
      setError("You do not have permission to add payment records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (
      !newPayment.paid_date ||
      !newPayment.pay_type ||
      !newPayment.paid_amount
    ) {
      setError("Please fill all payment fields.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const paidAmount = parseFloat(newPayment.paid_amount);
    if (isNaN(paidAmount) || paidAmount <= 0) {
      setError("Paid amount must be a valid number greater than zero.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const totalPaid = currentTotalPaid + paidAmount;
    if (totalPaid > (selectedInvoice?.net_total || 0)) {
      setError("Total paid amount cannot exceed the total amount.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const payment = {
      invoice_number: selectedInvoice.invoice_number,
      paid_date: newPayment.paid_date,
      pay_type: newPayment.pay_type,
      paid_amount: paidAmount,
      created_by: localStorage.getItem("user_id") || "system",
      tempId: Date.now(),
    };
    console.log("Adding payment to pending list:", payment);
    setPendingPayments([...pendingPayments, payment]);
    setNewPayment({
      paid_date: new Date().toISOString().split("T")[0],
      pay_type: "",
      paid_amount: "",
    });
    setError(null);
  };

  // Handle editing a payment
  const handleEditPayment = (payment, index) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Sale Pharmacy page");
      setError("You do not have permission to update payment records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Editing payment:", payment);
    setEditingPayment({ ...payment, index });
    setNewPayment({
      paid_date: payment.paid_date,
      pay_type: payment.pay_type,
      paid_amount: payment.paid_amount.toString(),
    });
  };

  // Handle saving edited payment
  const handleSaveEditPayment = () => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Sale Pharmacy page");
      setError("You do not have permission to update payment records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (
      !newPayment.paid_date ||
      !newPayment.pay_type ||
      !newPayment.paid_amount
    ) {
      setError("Please fill all payment fields.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const paidAmount = parseFloat(newPayment.paid_amount);
    if (isNaN(paidAmount) || paidAmount <= 0) {
      setError("Paid amount must be a valid number greater than zero.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    // Calculate total without the current editing payment
    const currentPaymentsWithoutEdit = getCurrentPayments.filter((p) =>
      editingPayment._id
        ? p._id !== editingPayment._id
        : p.tempId !== editingPayment.tempId
    );
    const totalPaid =
      currentPaymentsWithoutEdit.reduce(
        (sum, payment) => sum + parseFloat(payment.paid_amount || 0),
        0
      ) + paidAmount;
    if (totalPaid > (selectedInvoice?.net_total || 0)) {
      setError("Total paid amount cannot exceed the total amount.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const updatedPayment = {
      invoice_number: selectedInvoice.invoice_number,
      paid_date: newPayment.paid_date,
      pay_type: newPayment.pay_type,
      paid_amount: paidAmount,
      created_by:
        editingPayment.created_by ||
        localStorage.getItem("user_id") ||
        "system",
      ...(editingPayment._id
        ? { _id: editingPayment._id }
        : { tempId: editingPayment.tempId }),
    };
    console.log("Saving edited payment:", updatedPayment);
    if (editingPayment._id) {
      setUpdatedPayments([
        ...updatedPayments.filter((p) => p._id !== editingPayment._id),
        updatedPayment,
      ]);
    } else {
      setPendingPayments(
        pendingPayments.map((p) =>
          p.tempId === editingPayment.tempId ? updatedPayment : p
        )
      );
    }
    setEditingPayment(null);
    setNewPayment({
      paid_date: new Date().toISOString().split("T")[0],
      pay_type: "",
      paid_amount: "",
    });
    setError(null);
  };

  // Handle deleting a payment
  const handleDeletePayment = (payment, index) => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Sale Pharmacy page");
      setError("You do not have permission to delete payment records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Deleting payment:", payment);
    if (payment._id) {
      setDeletedPayments([...deletedPayments, payment._id]);
      setUpdatedPayments(updatedPayments.filter((p) => p._id !== payment._id));
    } else {
      setPendingPayments(
        pendingPayments.filter((p) => p.tempId !== payment.tempId)
      );
    }
    if (
      editingPayment &&
      (editingPayment._id === payment._id ||
        editingPayment.tempId === payment.tempId)
    ) {
      setEditingPayment(null);
      setNewPayment({
        paid_date: new Date().toISOString().split("T")[0],
        pay_type: "",
        paid_amount: "",
      });
    }
  };

  // Filter by Homeo (JMCP) or Yoga (AAFC)
  const handleResetFilter = () => {
    console.log("Resetting filter to All");
    setFilterType("All");
    setCurrentPage(1); // Reset to first page when filter changes
    // No need to modify responseData; filteredSale will recompute with sorted responseData
  };

  const handleHomeoFilter = () => {
    console.log("Filtering by Homeo (4HH)");
    setFilterType("Homeo");
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleYogaFilter = () => {
    console.log("Filtering by Yoga (4HY)");
    setFilterType("Yoga");
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle submitting all payment changes
  const handleSubmitPayments = async () => {
    if (
      !hasPermission("create") &&
      !hasPermission("update") &&
      !hasPermission("delete")
    ) {
      console.warn(
        "Create/Update/Delete permission denied for Sale Pharmacy page"
      );
      setError("You do not have permission to modify payment records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (
      pendingPayments.length === 0 &&
      updatedPayments.length === 0 &&
      deletedPayments.length === 0
    ) {
      setError("No payment changes to submit.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    try {
      console.log(
        "Submitting payment changes for invoice:",
        selectedInvoice.invoice_number
      );

      const hasExistingPayments =
        selectedInvoice?.sales_pharmacy_payment?.length > 0 ||
        updatedPayments.length > 0 ||
        deletedPayments.length > 0;

      // Handle initial or new payments with POST
      if (pendingPayments.length > 0 && hasPermission("create")) {
        const paymentsData = {
          invoice_number: selectedInvoice.invoice_number,
          payments: pendingPayments.map(({ tempId, ...payment }) => payment),
        };
        console.log("POST payload:", JSON.stringify(paymentsData, null, 2));
        const paymentResponse = await axios.post(
          `${Apiurl}/sale_payment_pharmacy`,
          paymentsData
        );
        console.log(
          "Payments submitted:",
          JSON.stringify(paymentResponse.data, null, 2)
        );
      }
      fetchData();

      // Handle updated payments with PUT
      if (
        hasExistingPayments &&
        updatedPayments.length > 0 &&
        hasPermission("update")
      ) {
        for (const payment of updatedPayments) {
          const { _id, ...paymentData } = payment;
          console.log(
            `PUT payload for payment ${_id}:`,
            JSON.stringify(paymentData, null, 2)
          );
          const updateResponse = await axios.put(
            `${Apiurl}/sale_payment_pharmacy/${_id}`,
            paymentData
          );
          console.log(
            "Payment updated:",
            JSON.stringify(updateResponse.data, null, 2)
          );
        }
      }

      // Handle deleted payments with DELETE
      if (
        hasExistingPayments &&
        deletedPayments.length > 0 &&
        hasPermission("delete")
      ) {
        for (const paymentId of deletedPayments) {
          console.log(`Deleting payment ID: ${paymentId}`);
          const deleteResponse = await axios.delete(
            `${Apiurl}/sale_payment_pharmacy/${paymentId}`
          );
          console.log(
            "Payment deleted:",
            JSON.stringify(deleteResponse.data, null, 2)
          );
        }
      }

      // Update sale record with new paid and balance amounts
      const totalPaid = currentTotalPaid;
      const updatedSale = {
        ...selectedInvoice,
        paid: totalPaid,
        balance: selectedInvoice.net_total - totalPaid,
      };
      console.log("PUT sale payload:", JSON.stringify(updatedSale, null, 2));
      const saleResponse = await axios.put(
        `${Apiurl}/sale_pharmacy/${selectedInvoice._id}`,
        updatedSale
      );
      console.log("Sale updated:", JSON.stringify(saleResponse.data, null, 2));

      setPendingPayments([]);
      setUpdatedPayments([]);
      setDeletedPayments([]);
      setEditingPayment(null);
      setNewPayment({
        paid_date: new Date().toISOString().split("T")[0],
        pay_type: "",
        paid_amount: "",
      });
      fetchData();
      setShowAddModal(false);
      setSelectedInvoice(null);
      setError(null);
    } catch (error) {
      console.error(
        "Error submitting payment changes:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to submit payment changes. Please try again.");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Initialize privileges and data
  useEffect(() => {
    console.log("Fetching initial privileges...");
    fetchUserPrivileges();
  }, []);

  useEffect(() => {
    if (!loadingPrivileges && hasPermission("access")) {
      console.log("Access granted for Sale Pharmacy page");
      if (hasPermission("read")) {
        console.log("Fetching sale data due to read permission");
        fetchData();
      } else {
        console.warn("Read permission denied for Sale Pharmacy page");
        setError("You do not have permission to view sale records.");
        setTimeout(() => setError(null), 3000);
      }
    } else if (!loadingPrivileges) {
      console.warn("Access permission denied for Sale Pharmacy page");
      setError("You do not have permission to access this page.");
      setTimeout(() => setError(null), 3000);
    }
  }, [userPrivileges, loadingPrivileges]);


  const toDateKey = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return "";
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getLocalDateKey = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const matchesDateFilter = (value) => {
    if (dateFilter === "all") return true;

    const itemDate = toDateKey(value);
    if (!itemDate) return false;

    const today = new Date();
    const todayKey = getLocalDateKey(today);

    if (dateFilter === "today") return itemDate === todayKey;

    if (dateFilter === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return itemDate === getLocalDateKey(yesterday);
    }

    if (dateFilter === "last_week") {
      const start = new Date(today);
      start.setDate(today.getDate() - 6);
      return itemDate >= getLocalDateKey(start) && itemDate <= todayKey;
    }

    if (dateFilter === "last_month") {
      const start = new Date(today);
      start.setDate(today.getDate() - 29);
      return itemDate >= getLocalDateKey(start) && itemDate <= todayKey;
    }

    if (dateFilter === "custom") {
      if (!customFromDate && !customToDate) return true;
      if (customFromDate && itemDate < customFromDate) return false;
      if (customToDate && itemDate > customToDate) return false;
      return true;
    }

    return true;
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    setCurrentPage(1);
  };

  // Filter data
  const filteredSale = useMemo(() => {
    let result = responseData;

    // Apply Homeo/Yoga filter based on bill_invoice_no or patient_id
    if (filterType === "Homeo") {
  result = result.filter(
    (item) =>
      item.bill_invoice_no?.startsWith("4HH-") ||
      item.patient_details?.[0]?.patient_id?.startsWith("4HH-")
  );
} else if (filterType === "Yoga") {
  result = result.filter(
    (item) =>
      item.bill_invoice_no?.startsWith("4HY-") ||
      item.patient_details?.[0]?.patient_id?.startsWith("4HY-")
  );
}

    // Apply date filter (Today is the default view).
    result = result.filter((item) => matchesDateFilter(item.sale_date));

    // Apply search term filter
    result = result.filter((item) =>
      [
        item.invoice_number,
        item.sale_date,
        item.patient_details?.[0]?.first_name,
        item.patient_details?.[0]?.last_name,
        item.patient_details?.[0]?.referred_by_name,
        item.net_total?.toString(),
      ].some((prop) =>
        prop != null
          ? prop.toString().toLowerCase().includes(searchTerm.toLowerCase())
          : false
      )
    );

    console.log("Filtered sale data:", result);
    return result;
  }, [
    responseData,
    searchTerm,
    filterType,
    dateFilter,
    customFromDate,
    customToDate,
  ]);

  // Pagination logic
  useEffect(() => {
    console.log("Updating items per page to:", showEntries);
    if (showEntries === "All") {
      setItemsPerPage(filteredSale.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
  }, [showEntries, filteredSale]);

  useEffect(() => {
    console.log(
      "Resetting current page due to searchTerm, itemsPerPage, or filterType change"
    );
    setCurrentPage(1);
  }, [
    searchTerm,
    itemsPerPage,
    filterType,
    dateFilter,
    customFromDate,
    customToDate,
  ]);

  const totalPages = Math.ceil(filteredSale.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSale.slice(indexOfFirstItem, indexOfLastItem);

  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    console.log("Generated page list:", pages);
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      console.log("Navigating to page:", page);
      setCurrentPage(page);
    }
  };

  // Other handlers
  const handleAddSaleClick = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Sale Pharmacy page");
      setError("You do not have permission to add sale records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Navigating to Add Sale Pharmacy page");
    toggleSidebar();
    navigate("/pharmacy/sale-pharmacy/add-sale-pharmacy");
  };

  const handleEdit = (sale) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Sale Pharmacy page");
      setError("You do not have permission to update sale records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Navigating to Update Sale Pharmacy page for ID:", sale._id);
    toggleSidebar();
    navigate(`/pharmacy/sale-pharmacy/update-sale-pharmacy/${sale._id}`, {
      state: { sale },
    });
  };

  const handlePrintView = (sale) => {
    if (!hasPermission("download")) {
      console.warn("Download permission denied for Sale Pharmacy page");
      setError("You do not have permission to print sale records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    try {
      const url = `/pharmacy/sale-pharmacy/sale-bill/${sale.invoice_number}`;
      const printWindow = window.open(url, "_blank");
      if (!printWindow) {
        alert("Popup blocked. Please allow popups for this site.");
        return;
      }
    } catch (error) {
      console.error("Error opening print view:", error);
      setError("Failed to open print view.");
      setTimeout(() => setError(null), 3000);
    }
  };

  const openDeleteModal = (id) => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Sale Pharmacy page");
      setError("You do not have permission to delete sale records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid sale record ID:", id);
      setError("Invalid sale record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Delete Sale modal for ID:", id);
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    console.log("Closing Delete Sale modal");
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  const confirmDelete = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Sale Pharmacy page");
      setError("You do not have permission to delete sale records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid sale record ID:", deleteId);
      setError("Invalid sale record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoadingDelete(true);
    try {
      console.log("Deleting sale with ID:", deleteId);
      await axios.delete(`${Apiurl}/sale_pharmacy/${deleteId}`);
      fetchData();
      closeDeleteModal();
    } catch (error) {
      console.error(
        "Error deleting sale:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to delete sale. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingDelete(false);
    }
  };

  if (loadingPrivileges) {
    return <div className="p-3 text-center">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_name:",
      localStorage.getItem("role_name"),
      "on Sale Pharmacy page"
    );
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 px-2">
      <style>{`
        .btn-custom {
          background-color: #01AEBE;
          color: white;
          border: none;
        }
        .btn-custom:hover {
          background-color: #018A97;
          color: white;
        }
      `}</style>

      {hasPermission("create") && (
        <div className="d-flex justify-content-end align-items-center mb-3">
          <button
            className="btn btn-custom btn-sm"
            onClick={handleAddSaleClick}
          >
            <i className="fas fa-plus me-2"></i> Add Sale
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {hasPermission("read") && (
        <div className="card card-body mt-2">

            <div className="d-flex flex-wrap align-items-center gap-2 mb-3 crm-filter-tabs">
              {[
                ["today", "Today"],
                ["yesterday", "Yesterday"],
                ["last_week", "Last Week"],
                ["last_month", "Last Month"],
                ["all", "All"],
                ["custom", "Custom Date"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`btn btn-sm ${
                    dateFilter === value ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => handleDateFilterChange(value)}
                >
                  {label}
                </button>
              ))}

              {dateFilter === "custom" && (
                <div className="d-flex flex-wrap align-items-center gap-2 ms-md-2">
                  <div className="d-flex align-items-center gap-1">
                    <label className="small fw-semibold text-muted mb-0">From</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customFromDate}
                      onChange={(e) => {
                        setCustomFromDate(e.target.value);
                        handleDateFilterChange("custom");
                      }}
                    />
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <label className="small fw-semibold text-muted mb-0">To</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customToDate}
                      onChange={(e) => {
                        setCustomToDate(e.target.value);
                        handleDateFilterChange("custom");
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          <div className="row mb-2 crm-table-toolbar">
            <div className="col-md-2">
              <div className="input-group">
                <span className="input-group-text">
                  <Table2 size={16} />
                </span>
                <select
                  className="form-select"
                  value={showEntries}
                  onChange={(e) => {
                    console.log(
                      "Changing entries per page to:",
                      e.target.value
                    );
                    setShowEntries(e.target.value);
                  }}
                >
                  <option value="10">Show 10</option>
                  <option value="20">Show 20</option>
                  <option value="50">Show 50</option>
                  <option value="100">Show 100</option>
                  <option value="200">Show 200</option>
                  <option value="500">Show 500</option>
                  <option value="1000">Show 1000</option>
                  <option value="All">Show All</option>
                </select>
              </div>
            </div>
            <div className="col-md-2"></div>
            <div className="col-md-2 rounded-5">
              <div className="btn-group crm-filter-tabs" role="group">
                <button
                  className={`btn btn-sm ${
                    filterType === "All" ? "btn-success" : "btn-outline-success"
                  }`}
                  onClick={handleResetFilter}
                >
                  All
                </button>
                <button
                  className={`btn btn-sm ${
                    filterType === "Homeo"
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={handleHomeoFilter}
                >
                  Homeo
                </button>
                <button
                  className={`btn btn-sm ${
                    filterType === "Yoga"
                      ? "btn-secondary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={handleYogaFilter}
                >
                  Yoga
                </button>
              </div>
            </div>

            <div className="col-md-3 offset-md-3">
              <div className="d-flex align-items-center">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control pe-5"
                    placeholder="Search by invoice, date, patient"
                    value={searchTerm}
                    onChange={(e) => {
                      console.log("Search term changed to:", e.target.value);
                      setSearchTerm(e.target.value);
                    }}
                    style={{ width: "250px", paddingRight: "2.5rem" }}
                  />
                  {searchTerm && (
                    <button
                      className="position-absolute bg-transparent border-0"
                      style={{
                        right: "30px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                      onClick={() => setSearchTerm("")}
                    >
                      <i className="fas fa-times text-muted"></i>
                    </button>
                  )}
                  <Search
                    size={16}
                    className="position-absolute text-muted"
                    style={{
                      right: "10px",
                      top: "35%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {loading && <div className="text-center py-3">Loading...</div>}

          <div className="table-responsive mt-3">
            <table className="table table-hover">
              <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                <tr>
                  <th>S.No</th>
                  <th>Sales Date</th>
                  <th>Invoice No</th>
                  <th>Patient Id</th>
                  <th>Patient Name</th>
                  <th>Category</th>
                  <th>Total Amount (₹)</th>
                  {(hasPermission("download") ||
                    hasPermission("update") ||
                    hasPermission("delete")) && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <tr key={item._id || index} className="align-middle">
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>{item.sale_date || "N/A"}</td>
                      <td>{item.invoice_number || "N/A"}</td>
                      <td>{item.patient_details?.[0]?.patient_id || "N/A"}</td>
                      <td>
                        {item.patient_details?.[0]
                          ? `${item.patient_details[0].first_name || ""} ${
                              item.patient_details[0].last_name || ""
                            }`.trim() || "N/A"
                          : "N/A"}
                      </td>
                      <td>
                        {item.patient_details?.[0]?.patient_type || "N/A"}
                      </td>
                      <td>
                        {item.net_total != null
                          ? `₹${Number(item.net_total).toFixed(2)}`
                          : "N/A"}
                      </td>
                      {(hasPermission("download") ||
                        hasPermission("update") ||
                        hasPermission("delete")) && (
                        <td>
                          <button
                            className="btn btn-sm p-1 border-0"
                            title="Print"
                            onClick={() => handlePrintView(item)}
                            disabled={!item.invoice_number}
                          >
                            <Printer size={16} className="text-secondary" />
                          </button>
                          {hasPermission("update") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Edit"
                              onClick={() => handleEdit(item)}
                              disabled={!item._id}
                            >
                              <Edit size={16} className="text-primary" />
                            </button>
                          )}
                          {hasPermission("delete") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Delete"
                              onClick={() => openDeleteModal(item._id)}
                              disabled={!item._id}
                            >
                              <Trash2 size={16} className="text-danger" />
                            </button>
                          )}
                          {hasPermission("create") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Add Payment"
                              onClick={() => handlePaymentClick(item)}
                              disabled={!item.invoice_number}
                            >
                              <IndianRupee size={16} className="text-success" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={
                        hasPermission("download") ||
                        hasPermission("update") ||
                        hasPermission("delete")
                          ? 8
                          : 7
                      }
                      className="text-center py-4"
                    >
                      {loading ? (
                        "Loading..."
                      ) : (
                        <div className="text-muted">
                          <i className="fas fa-search fa-2x mb-2"></i>
                          <br />
                          No sale records found matching your search criteria
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredSale.length > 0 && showEntries !== "All" && (
            <div className="container">
              <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    currentPage > 1 && handlePageClick(currentPage - 1)
                  }
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                  <span className="d-none d-sm-inline">Previous</span>
                </button>

                <div
                  className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                  style={{ maxWidth: "100%" }}
                >
                  {pages.map((page, index) => (
                    <button
                      key={index}
                      className={`btn btn-sm px-3 flex-shrink-0 ${
                        page === currentPage
                          ? "text-white btn-custom"
                          : "btn-outline-secondary"
                      }`}
                      onClick={() => handlePageClick(page)}
                      disabled={page === "..."}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    currentPage < totalPages && handlePageClick(currentPage + 1)
                  }
                  disabled={currentPage === totalPages}
                >
                  <span className="d-none d-sm-inline">Next</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deleteSaleModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <AlertTriangle size={20} style={{ color: "#dc2626" }} />
                  </span>
                </div>
                <h5 className="modal-title" id="deleteSaleModalLabel">
                  Delete Sale
                </h5>
                <p style={{ marginTop: "10px" }}>
                  Are you sure you want to delete this sale record?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-custom btn-sm"
                  onClick={confirmDelete}
                  disabled={loadingDelete}
                >
                  {loadingDelete ? "Deleting..." : "Yes, Delete It!"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasPermission("create") && showAddModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          aria-labelledby="addPaymentModalLabel"
          aria-hidden="false"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            transition: "opacity 0.3s ease-in-out",
          }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold" id="addPaymentModalLabel">
                  Add Payment Details
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedInvoice(null);
                    setPendingPayments([]);
                    setUpdatedPayments([]);
                    setDeletedPayments([]);
                    setEditingPayment(null);
                    setNewPayment({
                      paid_date: new Date().toISOString().split("T")[0],
                      pay_type: "",
                      paid_amount: "",
                    });
                  }}
                ></button>
              </div>

              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-lg-4">
                    <label className="form-label fw-bold">Invoice Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={selectedInvoice?.invoice_number || "N/A"}
                      disabled
                    />
                  </div>
                  <div className="col-lg-4">
                    <label className="form-label fw-bold">Patient Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={
                        selectedInvoice?.patient_details?.[0]
                          ? `${
                              selectedInvoice.patient_details[0].first_name ||
                              ""
                            } ${
                              selectedInvoice.patient_details[0].last_name || ""
                            }`.trim() || "N/A"
                          : "N/A"
                      }
                      disabled
                    />
                  </div>
                  <div className="col-lg-4">
                    <label className="form-label fw-bold">Patient Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={
                        selectedInvoice?.patient_details?.[0]?.mobile_no ||
                        "N/A"
                      }
                      disabled
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-2 mb-2">
                    <label className="form-label fw-bold">Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="paid_date"
                      value={newPayment.paid_date}
                      onChange={handlePaymentInputChange}
                    />
                  </div>
                  <div className="col-lg-2 mb-2">
                    <label className="form-label fw-bold">Total Amount</label>
                    <input
                      type="text"
                      className="form-control"
                      value={
                        selectedInvoice?.net_total != null
                          ? `₹${Number(selectedInvoice.net_total).toFixed(2)}`
                          : "N/A"
                      }
                      readOnly
                    />
                  </div>
                  <div className="col-lg-2 mb-2">
                    <label className="form-label fw-bold">Payment Type</label>
                    <div className="position-relative">
                      <select
                        className="form-control"
                        name="pay_type"
                        value={newPayment.pay_type}
                        onChange={handlePaymentInputChange}
                      >
                        <option value="">Payment Type</option>
                        <option value="Cash">Cash</option>
                        <option value="Card">Card</option>
                        <option value="Gpay">Gpay</option>
                        <option value="Phonepe">Phonepe</option>
                        <option value="Paytm">Paytm</option>
                        <option value="Amazon Pay">Amazon Pay</option>
                        <option value="UPI">UPI</option>
                      </select>
                      <i className="bi bi-caret-down-fill position-absolute end-0 top-50 translate-middle-y me-3"></i>
                    </div>
                  </div>
                  <div className="col-lg-2 mb-2">
                    <label className="form-label fw-bold">Paid Amount</label>
                    <input
                      type="number"
                      className="form-control"
                      name="paid_amount"
                      value={newPayment.paid_amount}
                      onChange={handlePaymentInputChange}
                      placeholder="Paid Amount"
                    />
                  </div>
                  <div className="col-lg-2 mb-2">
                    <label className="form-label fw-bold">Balance Amount</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`₹${currentBalance.toFixed(2)}`}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-1 mb-2 d-flex align-items-center justify-content-center">
                    {editingPayment ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSaveEditPayment}
                      >
                        Save
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleAddPayment}
                      >
                        ADD
                      </button>
                    )}
                  </div>
                </div>

                <div className="table-responsive mt-3">
                  <table className="table table-bordered">
                    <thead className="text-center">
                      <tr>
                        {[
                          "S.No",
                          "Paid Date",
                          "Pay Type",
                          "Amount",
                          "Action",
                        ].map((h, idx) => (
                          <th
                            key={idx}
                            style={{
                              backgroundColor: "#01AEBE",
                              color: "white",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="text-center">
                      {getCurrentPayments.length > 0 ? (
                        getCurrentPayments.map((item, index) => (
                          <tr key={item._id || item.tempId || index}>
                            <td>{index + 1}</td>
                            <td>{item.paid_date || "N/A"}</td>
                            <td>{item.pay_type || "N/A"}</td>
                            <td>
                              {item.paid_amount != null
                                ? `₹${Number(item.paid_amount).toFixed(2)}`
                                : "N/A"}
                            </td>
                            <td>
                              {hasPermission("update") && (
                                <button
                                  className="btn btn-sm p-1 border-0"
                                  style={{ backgroundColor: "transparent" }}
                                  onClick={() => handleEditPayment(item, index)}
                                  title="Edit Payment"
                                >
                                  <Edit size={16} className="text-primary" />
                                </button>
                              )}
                              {hasPermission("delete") && (
                                <button
                                  className="btn btn-sm p-1 border-0"
                                  style={{ backgroundColor: "transparent" }}
                                  onClick={() =>
                                    handleDeletePayment(item, index)
                                  }
                                  title="Delete Payment"
                                >
                                  <Trash2 size={16} className="text-danger" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No payments added
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="text-center mt-3">
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleSubmitPayments}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showAddModal || (showDeleteModal && hasPermission("delete"))) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}

export default SalePharmacy;